import { ROUTE_PATHS } from '../routes/routePaths';
import authService from '../services/authService';
import { clearAllCookies } from '../util';
import { isKeycloakLoginEnabled } from '../util/constants';
import { logger } from '../util/logger';
import { clearFreeTrialCache, clearProductEnablementCache } from '../util/posthog';
import { clearPartialAuth } from './partialAuthStorage';
import {
  getCurrentClientUserManager,
  KEYCLOAK_LOGIN_CLIENT_KEY,
  userManager,
  viewerUserManager,
} from './userManager';

const ANNOUNCEMENT_POP_SHOW_KEY = 'announcementPopShow';

const clearLocalSession = async (): Promise<void> => {
  try {
    const pendingRedirectUrl = localStorage.getItem('pendingRedirectUrl');

    await userManager.removeUser();
     await viewerUserManager.removeUser();

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('scope');
    localStorage.removeItem('session_state');
    localStorage.removeItem(KEYCLOAK_LOGIN_CLIENT_KEY);

    localStorage.removeItem('loginUserId');
    localStorage.removeItem('loginUserOrgId');
    localStorage.removeItem('loginUserOrgType');
    localStorage.removeItem('userData');
    sessionStorage.removeItem(ANNOUNCEMENT_POP_SHOW_KEY);

    if (pendingRedirectUrl) {
      localStorage.setItem('pendingRedirectUrl', pendingRedirectUrl);
    }

    clearAllCookies();

    // Clear OIDC/Keycloak session flags so re-login uses correct client (avoids "client_id mismatch on settings vs. signin state")
    sessionStorage.removeItem('viewer_client_redirect');
    sessionStorage.removeItem('keycloak_logout_pending');
    clearPartialAuth();
    clearFreeTrialCache();
    clearProductEnablementCache();
  } catch (error) {
    logger.error('[OIDC] Failed to clear local session:', error);
    throw error;
  }
};

/**
* Calls signoutRedirect() to end the Keycloak server-side session.
* Uses id_token_hint when available to skip logout confirmation; otherwise
* redirects without the hint so SSO can still be cleared (e.g. no local tokens).
*/
const performKeycloakLogout = async (): Promise<void> => {
  const { userManager: activeUserManager, clientId } = getCurrentClientUserManager();
  // Read id_token before clearLocalSession; Keycloak uses it for id_token_hint.
  const user = await activeUserManager.getUser();
  const idToken = (user?.id_token || localStorage.getItem('id_token'))?.trim() || null;

  await clearLocalSession();

  const postLogoutRedirectUri = `${window.location.origin}${ROUTE_PATHS.LOGIN}`;

  if (!idToken || idToken.length === 0) {
    try {
      sessionStorage.setItem('keycloak_logout_pending', 'true');
      await activeUserManager.signoutRedirect({
        post_logout_redirect_uri: postLogoutRedirectUri,
        extraQueryParams: clientId ? { client_id: clientId } : undefined,
      });
    } catch (error) {
      logger.error('[OIDC] Logout redirect (no id_token) failed:', error);
      sessionStorage.removeItem('keycloak_logout_pending');
      // eslint-disable-next-line require-atomic-updates
      window.location.href = ROUTE_PATHS.LOGIN;
    }
    return;
  }

  try {
    // Flag so the Login page can add logout=true when it next calls signinRedirect.
    sessionStorage.setItem('keycloak_logout_pending', 'true');

    await activeUserManager.signoutRedirect({
      id_token_hint: idToken,
      post_logout_redirect_uri: postLogoutRedirectUri,
      extraQueryParams: clientId ? { client_id: clientId } : undefined,
    });
  } catch (error) {
    logger.error('[OIDC] Logout redirect failed:', error);
    sessionStorage.removeItem('keycloak_logout_pending');
    await clearLocalSession();
    // eslint-disable-next-line require-atomic-updates
    window.location.href = ROUTE_PATHS.LOGIN;
    throw error;
  }
};

  //logout function that handles both Keycloak and manual login modes.
export const logout = async (isCompleteLogout: boolean = true): Promise<void> => {
  const useKeycloak = isKeycloakLoginEnabled();


  try {
    await authService.logout(isCompleteLogout);
  } catch (error) {
    logger.error('[Logout] Logout API call failed:', error);
  }

  if (useKeycloak) {
    // Keycloak: must always run so SSO session is ended; otherwise re-login hits "invalid client" / internal error
    await performKeycloakLogout();
    return;
  }

  // Non-Keycloak / manual login mode
  try {
    authService.customLogout();
    // eslint-disable-next-line require-atomic-updates
    window.location.href = ROUTE_PATHS.LOGIN;
  } catch (error) {
    logger.error(
      '[Logout] Local logout failed, clearing local storage anyway:',
      error
    );

    authService.customLogout();
    // eslint-disable-next-line require-atomic-updates
    window.location.href = ROUTE_PATHS.LOGIN;
  }
};


export const logoutWithoutApi = async (): Promise<void> => {
  const useKeycloak = isKeycloakLoginEnabled();

  if (useKeycloak) {
    await performKeycloakLogout();
    return;
  }

  try {
    authService.customLogout();
    // eslint-disable-next-line require-atomic-updates
    window.location.href = ROUTE_PATHS.LOGIN;
  } catch (error) {
    logger.error(
      '[LogoutWithoutApi] Local logout failed, clearing local storage anyway:',
      error
    );

    authService.customLogout();
    // eslint-disable-next-line require-atomic-updates
    window.location.href = ROUTE_PATHS.LOGIN;
  }
};